import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiAward, FiArrowRight } = FiIcons;

const GuaranteeSection = () => {
  const [showRandomFacts, setShowRandomFacts] = useState(false);

  const credentials = [
    {
      icon: FiAward,
      title: "Academic Excellence",
      description: "Trained at Purdue University, ranked globally among the top programs"
    },
    {
      icon: FiAward,
      title: "Elite Recognition",
      description: "David Knox Fellowship recipient (less than 0.02% awarded)"
    },
    {
      icon: FiAward,
      title: "Scientific Background",
      description: "Former NASA scientist specializing in brain and cognitive sciences"
    },
    {
      icon: FiAward,
      title: "Presidential Honor",
      description: "Outstanding Student Award recipient from President Obama"
    },
    {
      icon: FiAward,
      title: "Performance Expertise",
      description: "Specialist in executive function, traumatic brain injury, and elite athlete mental training"
    },
    {
      icon: FiAward,
      title: "Elite Athlete Understanding",
      description: "Trusted by Olympians, national champions, and pro athletes to elevate their game"
    }
  ];

  const randomFacts = [
    {
      fact: "Mildly obsessed with French fries",
      position: { top: "20%", left: "10%" }
    },
    {
      fact: "Whodunit lover",
      position: { top: "60%", right: "15%" }
    }
  ];

  return (
    <section id="guarantee" className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Guarantee Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-8">
            Perform Like a Champion in 30 Days or It's On Us
          </h2>
          <div className="card-featured max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-accent-magenta text-white p-4 rounded-full">
                <SafeIcon icon={FiShield} className="h-8 w-8" />
              </div>
            </div>
            <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-6">Our Promise:</h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              We believe in our brain training. If you don't notice faster decisions and improved pressure performance within 30 days, we'll work with you until you do — or offer a refund. Because your athletic success is that important.
            </p>
          </div>
        </motion.div>

        {/* Meet Sanna Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="relative">
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-gray-900 text-center mb-12">
              Meet <span className="relative inline-block">
                Sanna
                {/* Random facts - positioned with arrows */}
                <AnimatePresence>
                  {showRandomFacts && (
                    <>
                      {randomFacts.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.5, delay: index * 0.2 }}
                          className="absolute bg-white p-4 rounded-xl shadow-lg border border-accent-magenta/20 max-w-xs z-10"
                          style={item.position}
                        >
                          <div className="text-sm text-gray-700 font-medium">
                            {item.fact}
                          </div>
                          {/* Arrow pointing to name */}
                          <div className="absolute w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white transform rotate-45 -bottom-1 left-1/2 -translate-x-1/2"></div>
                        </motion.div>
                      ))}
                    </>
                  )}
                </AnimatePresence>
              </span> Darwish, M.S. CCC-SLP — Your Elite Performance Cognitive Coach
            </h3>
            
            <div className="text-center space-y-6">
              <div className="space-y-4">
                <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
                  This isn't generic sports psychology. It's neuroscience-based cognitive training designed specifically for athletes who demand excellence.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
                  Let's get your brain performing at championship level—starting today.
                </p>
              </div>
              <button
                onClick={() => setShowRandomFacts(!showRandomFacts)}
                className="btn-secondary flex items-center gap-2 mx-auto"
              >
                {showRandomFacts ? 'Hide' : 'Unlock'} random facts
                <SafeIcon icon={FiArrowRight} className={`h-5 w-5 transition-transform duration-200 ${showRandomFacts ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Credentials Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {credentials.map((credential, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-accent-gold/10 p-3 rounded-full flex-shrink-0">
                    <SafeIcon icon={credential.icon} className="h-6 w-6 text-accent-gold" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {credential.title}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {credential.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GuaranteeSection;